/**
 * HubDatePop — the two-rail DUE-DATE popover (the to-dos upgrade, Claude Design
 * handoff 2026-10-04: refactor/design-sync/todos-upgrade/, "Two-rail date
 * popover (every date chip)").
 *
 * WHY A NEW CANONICAL. `HubDateField` (shared/DateTimePicker) is the date+TIME
 * picker with an explicit Cancel/Save commit — right for an event, wrong for a
 * to-do's due DAY, where the design asks for one click: pick a preset or a day
 * and it applies and closes. It also knows two things a generic picker cannot:
 * a SUGGESTED date ("Confirm Oct 10" heads the rail while the date is still the
 * app's one-week suggestion) and an optional "Clear date".
 *
 * Geometry is the design's, verbatim: 396 wide, radius 6, hairline, the one
 * floating shadow; a 160px paper-2 rail of presets (Today · Tomorrow · This
 * Friday · Next Monday · In a week · In two weeks) on the left, the month on
 * the right (S–S, 27px cells, today outlined indigo, the chosen day filled,
 * past days ghost). It sits right-aligned under its anchor, clamped to the
 * viewport, and flips above when there is no room below. Tokens only.
 *
 * Values are LOCAL calendar days as `YYYY-MM-DD` — never a UTC timestamp, so
 * the day a person picks is the day that is stored (the evening-before trap).
 */
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { PopoverPortal } from '../shared/InlineEdit/popoverPortal';
import { HEAD } from '../v2/kit';

export interface HubDatePopProps {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  /** `YYYY-MM-DD`, or null when there is no date. */
  value: string | null;
  /** The value is the app's suggestion — offer "Confirm <date>" first. */
  suggested?: boolean;
  onPick: (isoDay: string) => void;
  /** Offer "Clear date" (only shown while there is a value). */
  onClear?: () => void;
  /** The rail's kicker: "Due", "Suggested", "Set a date". */
  header?: string;
}

const W = 396;
const H = 292;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (n: number) => String(n).padStart(2, '0');
export const isoDayOf = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDay = (iso: string | null): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const shortDate = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

/** Days until the next `weekday` (0 = Sun), never zero — "This Friday" on a
 *  Friday means next Friday, as the design's own helper does. */
function daysUntil(today: Date, weekday: number): number {
  const n = (weekday - today.getDay() + 7) % 7;
  return n === 0 ? 7 : n;
}

export function HubDatePop({ anchorRef, open, onClose, value, suggested, onPick, onClear, header }: HubDatePopProps): React.ReactElement | null {
  const today = useMemo(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = parseDay(value);
  const [cursor, setCursor] = useState(() => { const b = chosen ?? today; return { y: b.getFullYear(), m: b.getMonth() }; });
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const b = parseDay(value) ?? today;
    setCursor({ y: b.getFullYear(), m: b.getMonth() });
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8));
      const below = r.bottom + 5;
      const top = below + H > window.innerHeight - 8 ? Math.max(8, r.top - 5 - H) : below;
      setCoords({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open) return undefined;
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (iso: string) => { onPick(iso); onClose(); };
  const presets: Array<{ label: string; iso: string; meta?: string; strong?: boolean }> = [];
  if (suggested && chosen) presets.push({ label: `Confirm ${shortDate(chosen)}`, iso: isoDayOf(chosen), strong: true });
  const fri = addDays(today, daysUntil(today, 5));
  const mon = addDays(today, daysUntil(today, 1));
  presets.push(
    { label: 'Today', iso: isoDayOf(today), meta: DOW[today.getDay()] },
    { label: 'Tomorrow', iso: isoDayOf(addDays(today, 1)), meta: DOW[addDays(today, 1).getDay()] },
    { label: 'This Friday', iso: isoDayOf(fri), meta: shortDate(fri) },
    { label: 'Next Monday', iso: isoDayOf(mon), meta: shortDate(mon) },
    { label: 'In a week', iso: isoDayOf(addDays(today, 7)), meta: shortDate(addDays(today, 7)) },
    { label: 'In two weeks', iso: isoDayOf(addDays(today, 14)), meta: shortDate(addDays(today, 14)) },
  );

  const first = new Date(cursor.y, cursor.m, 1);
  const lead = first.getDay();
  const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const todayIso = isoDayOf(today);
  const nav = (dir: -1 | 1) => setCursor((c) => {
    const m = c.m + dir;
    return m < 0 ? { y: c.y - 1, m: 11 } : m > 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m };
  });

  const presetBtn: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', padding: '7px 11px',
    background: 'transparent', border: 'none', fontFamily: HEAD, fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer',
  };
  const navBtn: React.CSSProperties = {
    width: 24, height: 24, display: 'grid', placeItems: 'center', border: '1px solid var(--rule)', background: 'transparent',
    borderRadius: 4, cursor: 'pointer', color: 'var(--ink-soft)', padding: 0,
  };

  return (
    <PopoverPortal open coords={coords} animate ignoreRefs={[anchorRef]} onClickOutside={onClose}>
      <div
        role="dialog"
        aria-label={header || 'Pick a date'}
        data-hub-date-pop=""
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: W, display: 'flex', background: 'var(--paper)', border: '1px solid var(--rule)', borderRadius: 6, overflow: 'hidden', boxShadow: '0 8px 22px rgba(15,15,15,.14)', fontFamily: HEAD }}
      >
        <div style={{ width: 160, flex: 'none', borderRight: '1px solid var(--rule)', background: 'var(--paper-2)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '9px 11px 6px', fontWeight: 700, fontSize: 10.5, letterSpacing: '.13em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>
            {header || (suggested ? 'Suggested' : value ? 'Due' : 'Set a date')}
          </div>
          {presets.map((p) => {
            const on = p.iso === value;
            return (
              <button key={p.label} type="button" className="v2-item" onClick={() => pick(p.iso)} style={{ ...presetBtn, fontWeight: p.strong ? 700 : 500, color: p.strong ? 'var(--indigo)' : 'var(--ink)' }}>
                <span aria-hidden style={{ width: 6, height: 6, flex: 'none', transform: 'rotate(45deg)', background: on ? 'var(--indigo)' : 'transparent' }} />
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.label}</span>
                {p.meta && <span style={{ fontSize: 10.5, color: 'var(--ink-faint)', flex: 'none' }}>{p.meta}</span>}
              </button>
            );
          })}
          <span style={{ flex: 1 }} />
          {onClear && value && (
            <button type="button" className="v2-item" onClick={() => { onClear(); onClose(); }} style={{ ...presetBtn, padding: '8px 11px', borderTop: '1px solid var(--rule-soft)', color: 'var(--ink-faint)', fontWeight: 500 }}>
              <span aria-hidden style={{ width: 6, height: 6, flex: 'none' }} />
              Clear date
            </button>
          )}
        </div>
        <div style={{ flex: 1, padding: '10px 11px 11px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <button type="button" aria-label="Previous month" onClick={() => nav(-1)} style={navBtn}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 12.5, color: 'var(--ink)' }}>{MONTHS_LONG[cursor.m]} {cursor.y}</span>
            <button type="button" aria-label="Next month" onClick={() => nav(1)} style={navBtn}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
            </button>
          </div>
          <div role="grid" aria-label={`${MONTHS_LONG[cursor.m]} ${cursor.y}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <span key={i} style={{ display: 'grid', placeItems: 'center', height: 18, fontSize: 10.5, fontWeight: 800, color: 'var(--ink-ghost)' }}>{d}</span>
            ))}
            {Array.from({ length: lead }).map((_, i) => <span key={`lead-${i}`} />)}
            {Array.from({ length: days }).map((_, i) => {
              const iso = `${cursor.y}-${pad(cursor.m + 1)}-${pad(i + 1)}`;
              const on = iso === value;
              const isToday = iso === todayIso;
              const past = iso < todayIso;
              return (
                <button
                  key={iso}
                  type="button"
                  aria-label={iso}
                  aria-pressed={on}
                  className={on ? undefined : 'v2-item'}
                  onClick={() => pick(iso)}
                  style={{
                    height: 27, borderRadius: 3, cursor: 'pointer', fontFamily: HEAD, fontSize: 11, padding: 0,
                    fontWeight: on || isToday ? 800 : 600,
                    border: `1px solid ${isToday && !on ? 'var(--indigo)' : 'transparent'}`,
                    background: on ? 'var(--indigo)' : 'transparent',
                    color: on ? 'var(--on-brand)' : past ? 'var(--ink-ghost)' : 'var(--ink-soft)',
                  }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </PopoverPortal>
  );
}

export default HubDatePop;
